/**
 * Encompass eFolder tab component
 * Shows synced documents from Encompass in a resizable split view with preview pane
 * Supports copy mode for selecting and copying documents to other loans
 */

import React, { useState, useCallback, useMemo } from 'react';
import { Loader2, FileText } from 'lucide-react';
import { Panel, PanelGroup, PanelResizeHandle } from 'react-resizable-panels';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { useEncompassDocuments } from '@/hooks/useEncompassDocuments';
import { useEfolderFilters } from '@/hooks/useEfolderFilters';
import { useAuth } from '@/contexts/useAuth';
import { getAttachmentUrl, triggerDownload, resolveAttachmentFilename } from '@/services/encompassDocumentService';
import { EfolderSidebar } from './EfolderSidebar';
import { EfolderPreviewPane } from './EfolderPreviewPane';
import { CopyDocumentsModal } from './CopyDocumentsModal';
import type { EncompassAttachment } from '@/types/encompass-documents';

interface EncompassEfolderTabProps {
  loanId: string;
}

export function EncompassEfolderTab({ loanId }: EncompassEfolderTabProps) {
  const { user } = useAuth();
  const organizationId = user?.organization_id || '';

  const {
    documents,
    loading,
    error,
    refetch,
    syncDocuments,
    syncing
  } = useEncompassDocuments(loanId);

  // Filter and sort state (content-search RPC is keyed by loanId)
  const {
    filters,
    sort,
    updateFilter,
    setSortField,
    toggleSortDirection,
    resetFilters,
    filteredDocuments,
    groupedDocuments,
    totalAttachments,
    filteredAttachments,
    inactiveCount,
    contentSearchLoading,
    contentSearchError
  } = useEfolderFilters(documents, loanId);

  // UI state
  const [selectedAttachment, setSelectedAttachment] = useState<EncompassAttachment | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  // Copy mode state
  const [isCopyMode, setIsCopyMode] = useState(false);
  const [checkedAttachments, setCheckedAttachments] = useState<Set<string>>(new Set());
  const [copyModalOpen, setCopyModalOpen] = useState(false);
  const [downloading, setDownloading] = useState(false);

  // Get all downloaded attachments (for copy mode)
  const allDownloadedAttachments = useMemo(() => {
    const attachments: EncompassAttachment[] = [];
    for (const doc of documents) {
      for (const att of doc.attachments) {
        if (att.download_status === 'downloaded' && att.is_active !== false) {
          attachments.push(att);
        }
      }
    }
    return attachments;
  }, [documents]);

  // Get checked attachment objects for the modal
  const checkedAttachmentObjects = useMemo(() => {
    return allDownloadedAttachments.filter(att => checkedAttachments.has(att.id));
  }, [allDownloadedAttachments, checkedAttachments]);

  // Phase 6: O(1) lookup maps for attachment → group/folder relationships
  const attachmentToGroup = useMemo(() => {
    const map = new Map<string, string>();
    for (const doc of documents) {
      const group = doc.document_groups?.[0] || 'Other Documents';
      for (const att of doc.attachments || []) {
        map.set(att.id, group);
      }
    }
    return map;
  }, [documents]);

  // Track visible vs hidden selections (Phase 3 - filter/selection fix)
  // Selections can become hidden when user applies filters after selecting documents
  const { visibleSelections, hiddenSelections } = useMemo(() => {
    // Get IDs of all attachments visible with current filters
    const visibleIds = new Set(
      filteredDocuments.flatMap(d => d.attachments?.map(a => a.id) || [])
    );

    const visible = new Set<string>();
    const hidden = new Set<string>();

    checkedAttachments.forEach(id => {
      if (visibleIds.has(id)) {
        visible.add(id);
      } else {
        hidden.add(id);
      }
    });

    return { visibleSelections: visible, hiddenSelections: hidden };
  }, [checkedAttachments, filteredDocuments]);

  // Clear hidden selections (keep only visible ones)
  const handleClearHiddenSelections = useCallback(() => {
    setCheckedAttachments(visibleSelections);
  }, [visibleSelections]);

  // Phase 6: Group-level toggle handler
  const handleToggleGroupCheck = useCallback((groupName: string) => {
    const groupAttachmentIds = allDownloadedAttachments
      .filter(att => attachmentToGroup.get(att.id) === groupName)
      .map(a => a.id);

    const allSelected = groupAttachmentIds.length > 0 &&
      groupAttachmentIds.every(id => checkedAttachments.has(id));

    setCheckedAttachments(prev => {
      const next = new Set(prev);
      if (allSelected) {
        groupAttachmentIds.forEach(id => next.delete(id));
      } else {
        groupAttachmentIds.forEach(id => next.add(id));
      }
      return next;
    });
  }, [allDownloadedAttachments, attachmentToGroup, checkedAttachments]);

  // Phase 6: Folder-level toggle handler
  const handleToggleFolderCheck = useCallback((folderId: string) => {
    const folder = documents.find(doc => doc.id === folderId);
    const folderAttachmentIds = (folder?.attachments || [])
      .filter(att => att.download_status === 'downloaded' && att.is_active !== false)
      .map(a => a.id);

    const allSelected = folderAttachmentIds.length > 0 &&
      folderAttachmentIds.every(id => checkedAttachments.has(id));

    setCheckedAttachments(prev => {
      const next = new Set(prev);
      if (allSelected) {
        folderAttachmentIds.forEach(id => next.delete(id));
      } else {
        folderAttachmentIds.forEach(id => next.add(id));
      }
      return next;
    });
  }, [documents, checkedAttachments]);

  // Phase 6: Selection state helpers (none/partial/all)
  const getGroupSelectionState = useCallback((groupName: string): 'none' | 'partial' | 'all' => {
    const groupAttachments = allDownloadedAttachments
      .filter(att => attachmentToGroup.get(att.id) === groupName);

    if (groupAttachments.length === 0) {
return 'none';
}

    const selectedCount = groupAttachments.filter(a => checkedAttachments.has(a.id)).length;
    if (selectedCount === 0) {
return 'none';
}
    if (selectedCount === groupAttachments.length) {
return 'all';
}
    return 'partial';
  }, [allDownloadedAttachments, attachmentToGroup, checkedAttachments]);

  const getFolderSelectionState = useCallback((folderId: string): 'none' | 'partial' | 'all' => {
    const folder = documents.find(doc => doc.id === folderId);
    const copyableAttachments = (folder?.attachments || [])
      .filter(att => att.download_status === 'downloaded' && att.is_active !== false);

    if (copyableAttachments.length === 0) {
return 'none';
}

    const selectedCount = copyableAttachments.filter(a => checkedAttachments.has(a.id)).length;
    if (selectedCount === 0) {
return 'none';
}
    if (selectedCount === copyableAttachments.length) {
return 'all';
}
    return 'partial';
  }, [documents, checkedAttachments]);

  // Get copyable count for a group (downloaded + active only)
  // This ensures group checkbox is disabled when all files are inactive
  const getCopyableCountForGroup = useCallback((groupName: string): number => {
    return allDownloadedAttachments.filter(
      att => attachmentToGroup.get(att.id) === groupName
    ).length;
  }, [allDownloadedAttachments, attachmentToGroup]);

  // Handle attachment selection
  const handleSelectAttachment = useCallback(async (attachment: EncompassAttachment) => {
    setSelectedAttachment(attachment);

    if (attachment.download_status !== 'downloaded' || !attachment.storage_path) {
      setPreviewUrl(null);
      return;
    }

    setPreviewLoading(true);
    try {
      const url = await getAttachmentUrl(attachment.storage_path);
      setPreviewUrl(url);
    } catch {
      toast.error('Failed to load preview');
      setPreviewUrl(null);
    } finally {
      setPreviewLoading(false);
    }
  }, []);

  // Handle view: open signed URL in new tab (popup-blocker safe)
  const handleView = useCallback(async (attachment: EncompassAttachment) => {
    if (!attachment.storage_path) {
      toast.error('File not available for viewing');
      return;
    }
    // Open blank tab synchronously within user gesture context
    const tab = window.open('', '_blank');
    if (!tab) {
      toast.error('Popup blocked — please allow popups for this site');
      return;
    }
    try {
      const url = await getAttachmentUrl(attachment.storage_path);
      if (url) {
        tab.opener = null;
        tab.location.href = url;
      } else {
        tab.close();
        toast.error('Failed to load document');
      }
    } catch {
      tab.close();
      toast.error('Failed to load document');
    }
  }, []);

  // Handle download: blob download with correct filename
  const handleDownload = useCallback(async (attachment: EncompassAttachment) => {
    if (!attachment.storage_path) {
      toast.error('File not available for download');
      return;
    }
    try {
      const url = await getAttachmentUrl(attachment.storage_path);
      if (url) {
        const filename = resolveAttachmentFilename(attachment);
        await triggerDownload(url, filename);
        toast.success('Download started');
      }
    } catch {
      toast.error('Failed to download file');
    }
  }, []);

  // Handle manual sync
  const handleSync = useCallback(async () => {
    const result = await syncDocuments();
    if (result.success) {
      toast.success(result.message);
    } else {
      toast.error(result.message);
    }
  }, [syncDocuments]);

  // Toggle sidebar
  const handleToggleSidebar = useCallback(() => {
    setSidebarCollapsed(prev => !prev);
  }, []);

  // Copy mode handlers
  const handleEnterCopyMode = useCallback(() => {
    setIsCopyMode(true);
    setCheckedAttachments(new Set());
  }, []);

  const handleExitCopyMode = useCallback(() => {
    setIsCopyMode(false);
    setCheckedAttachments(new Set());
  }, []);

  const handleToggleCheck = useCallback((attachmentId: string) => {
    setCheckedAttachments(prev => {
      const next = new Set(prev);
      if (next.has(attachmentId)) {
        next.delete(attachmentId);
      } else {
        next.add(attachmentId);
      }
      return next;
    });
  }, []);

  const handleSelectAllDownloaded = useCallback(() => {
    setCheckedAttachments(new Set(allDownloadedAttachments.map(att => att.id)));
  }, [allDownloadedAttachments]);

  const handleOpenCopyModal = useCallback(() => {
    if (checkedAttachments.size > 0) {
      setCopyModalOpen(true);
    }
  }, [checkedAttachments.size]);

  const handleCopyComplete = useCallback(() => {
    handleExitCopyMode();
    // Optionally refetch to show updated state
    refetch();
  }, [handleExitCopyMode, refetch]);

  // Bulk download handler - downloads files individually (no ZIP)
  const handleBulkDownload = useCallback(async () => {
    if (checkedAttachments.size === 0) {
return;
}

    setDownloading(true);
    const attachmentsToDownload = checkedAttachmentObjects;
    const total = attachmentsToDownload.length;
    let completed = 0;
    let failed = 0;

    toast.info(`Downloading ${total} files...`);

    for (const att of attachmentsToDownload) {
      if (!att.storage_path) {
        failed++;
        continue;
      }

      try {
        const url = await getAttachmentUrl(att.storage_path);
        if (url) {
          await triggerDownload(url, resolveAttachmentFilename(att));
          completed++;
          // Small delay to prevent browser from blocking multiple downloads
          await new Promise(resolve => setTimeout(resolve, 300));
        } else {
          failed++;
        }
      } catch {
        failed++;
      }
    }

    setDownloading(false);

    if (failed === 0) {
      toast.success(`Downloaded ${completed} files`);
    } else if (completed > 0) {
      toast.warning(`Downloaded ${completed} files, ${failed} failed`);
    } else {
      toast.error('All downloads failed');
    }
  }, [checkedAttachments, checkedAttachmentObjects]);

  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center h-full py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-full py-12 text-center">
        <p className="text-red-500 mb-4">{error}</p>
        <Button variant="outline" onClick={refetch}>
          Try Again
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-[calc(100vh-122px)]">
      {/* Resizable split panel layout - no header, PDF starts immediately */}
      <PanelGroup direction="horizontal" className="h-full overflow-hidden">
          {/* Sidebar panel */}
          <Panel
            defaultSize={sidebarCollapsed ? 5 : 25}
            minSize={sidebarCollapsed ? 5 : 15}
            maxSize={sidebarCollapsed ? 5 : 40}
            className="transition-all duration-200"
          >
            <EfolderSidebar
              documents={filteredDocuments}
              groupedDocuments={groupedDocuments}
              selectedAttachmentId={selectedAttachment?.id || null}
              onSelectAttachment={handleSelectAttachment}
              onViewAttachment={handleView}
              onDownloadAttachment={handleDownload}
              sort={sort}
              onSortFieldChange={setSortField}
              onSortDirectionToggle={toggleSortDirection}
              isCollapsed={sidebarCollapsed}
              onToggleCollapse={handleToggleSidebar}
              filteredCount={filteredAttachments}
              totalCount={totalAttachments}
              filters={filters}
              onFilterChange={updateFilter}
              onResetFilters={resetFilters}
              inactiveCount={inactiveCount}
              // Copy mode props
              isCopyMode={isCopyMode}
              checkedAttachments={checkedAttachments}
              onEnterCopyMode={handleEnterCopyMode}
              onExitCopyMode={handleExitCopyMode}
              onToggleCheck={handleToggleCheck}
              onSelectAllDownloaded={handleSelectAllDownloaded}
              onOpenCopyModal={handleOpenCopyModal}
              downloadedCount={allDownloadedAttachments.length}
              // Hidden selections warning (Phase 3)
              hiddenSelectionsCount={hiddenSelections.size}
              onClearHiddenSelections={handleClearHiddenSelections}
              // Phase 6: Group/folder selection handlers
              onToggleGroupCheck={handleToggleGroupCheck}
              onToggleFolderCheck={handleToggleFolderCheck}
              getGroupSelectionState={getGroupSelectionState}
              getFolderSelectionState={getFolderSelectionState}
              getCopyableCountForGroup={getCopyableCountForGroup}
              // Bulk download
              onBulkDownload={handleBulkDownload}
              downloading={downloading}
              // Content-search state from the hook
              contentSearchLoading={contentSearchLoading}
              contentSearchError={contentSearchError}
            />
          </Panel>

          {/* Resize handle */}
          <PanelResizeHandle className="w-1 bg-border hover:bg-primary/50 transition-colors cursor-col-resize" />

          {/* Preview panel - PDF takes full height */}
          <Panel defaultSize={75} minSize={50} className="flex flex-col">
            <div className="relative flex-1 min-h-0 overflow-hidden">
              {/* Floating doc name badge - shows when sidebar collapsed */}
              {sidebarCollapsed && selectedAttachment && (
                <div className="absolute top-2 left-2 z-10 flex items-center gap-1.5 bg-background/90 backdrop-blur border rounded-md px-2 py-1 shadow-sm">
                  <FileText className="h-3.5 w-3.5 text-muted-foreground" />
                  <span className="text-sm font-medium truncate max-w-[200px]">
                    {selectedAttachment.title || 'Untitled'}
                  </span>
                </div>
              )}
              <EfolderPreviewPane
                selectedAttachment={selectedAttachment}
                previewUrl={previewUrl}
                previewLoading={previewLoading}
                onRetrySync={handleSync}
                syncing={syncing}
              />
            </div>
          </Panel>
        </PanelGroup>

      {/* Copy Documents Modal */}
      <CopyDocumentsModal
        isOpen={copyModalOpen}
        onClose={() => setCopyModalOpen(false)}
        selectedAttachments={checkedAttachmentObjects}
        sourceLoanId={loanId}
        organizationId={organizationId}
        onCopyComplete={handleCopyComplete}
        onUpdateSelection={setCheckedAttachments}
      />
    </div>
  );
}
